/**
 * Return paths: the in-app screen a coach goes back to after checkout. A locked
 * click carries the screen it was made on, billing forwards it, and checkout
 * sends her back there.
 *
 * One rule decides what counts as a return path, and both ends apply it: the
 * billing page to its `?returnTo=`, and the server to every URL it hands
 * Stripe. A crafted link can therefore never send a coach off the app, whatever
 * the client sent. Only coach screens under {@link RETURN_PATH_ROOT} qualify.
 *
 * Aliases: return path, returnTo, checkout return, success_url, cancel_url
 */

import { validateRedirectPath } from "~/lib/shared/post-auth-routing";

/** Longer than any screen's URL, and far inside Stripe's limit on its URLs. */
export const RETURN_PATH_MAX_LENGTH = 1024;

/**
 * Checkout returns only to screens under this path. The post-checkout re-syncs,
 * `NovaCheckoutReturn` and `AcceleratorCheckoutReturn`, are mounted in its
 * layout: a return anywhere else would skip them, and leave the coach behind
 * the lock she just paid to lift with the checkout markers still in the URL.
 */
export const RETURN_PATH_ROOT = "/my-coaching";

/**
 * Query keys a return from Stripe adds, and the app removes once it has
 * re-synced. A return path never keeps them: a screen reached mid-return would
 * otherwise send a later checkout back with an old session to confirm.
 */
export const CHECKOUT_RETURN_PARAMS = [
  "nova_subscribed",
  "nova_billing",
  "accelerator_purchased",
  "checkout_session_id",
] as const;

export const BILLING_PATH = "/settings/profile/billing";

/** The explanations the billing page shows when a checkout was refused. */
export const BILLING_NOTICES = [
  "nova-subscription-exists",
  "nova-subscription-unpaid",
  "plan-already-held",
] as const;
export type BillingNotice = (typeof BILLING_NOTICES)[number];

/** Parses a return path against a throwaway origin, so an escape shows up as
 *  a different origin rather than as a string that looks harmless. */
const BASE = "https://return-path.invalid";

/**
 * The pathname and search of the coach screen `value` names, or null when it
 * names anything else: a screen outside {@link RETURN_PATH_ROOT}, an absolute
 * or protocol-relative URL, a backslash or control-character trick, a
 * traversal, or a string too long to be a screen.
 *
 * The hash is dropped (no screen here is addressed by one), and so are the
 * {@link CHECKOUT_RETURN_PARAMS}. Everything else in the search is kept, so she
 * returns to the same filter, tab or week she left.
 */
export function parseReturnPath(value: unknown): string | null {
  if (typeof value !== "string" || value.length > RETURN_PATH_MAX_LENGTH)
    return null;
  if (validateRedirectPath(value) === null) return null;
  const url = new URL(value, BASE);
  if (url.origin !== BASE) return null;
  const { pathname } = url;
  const underRoot =
    pathname === RETURN_PATH_ROOT ||
    pathname.startsWith(`${RETURN_PATH_ROOT}/`);
  if (!underRoot) return null;
  // Deleting re-serialises the whole query, so only touch one that has a key
  // to remove; any other query keeps the encoding it arrived with.
  for (const key of CHECKOUT_RETURN_PARAMS) {
    if (url.searchParams.has(key)) url.searchParams.delete(key);
  }
  // The guard above saw the raw string, but the parser then resolves dot
  // segments, encoded ones too: `/%2e//evil.test` comes out as the
  // protocol-relative `//evil.test`. Guard what is returned as well.
  return validateRedirectPath(`${pathname}${url.search}`);
}

/**
 * The screen a checkout returns to, checked on the server rather than trusted
 * from the client. Undefined when the caller named none. A path that fails the
 * check is refused outright: the app only ever sends paths that pass it, so a
 * failure means a hand-made request.
 */
export function checkoutReturnPath(
  returnTo: string | undefined,
): string | undefined {
  if (returnTo === undefined) return undefined;
  const path = parseReturnPath(returnTo);
  if (path === null)
    throw new Error("Checkout can only return to a coaching page in the app.");
  return path;
}

/**
 * `path` with `params` added to its query, replacing any of the same name, and
 * joined with `?` or `&` as the path needs.
 *
 * Values are appended as they are, not encoded, so Stripe's
 * `{CHECKOUT_SESSION_ID}` template reaches Stripe intact to be filled in. Pass
 * only literals that are already safe in a URL.
 */
export function withQuery(
  path: string,
  params: Readonly<Record<string, string>>,
): string {
  const url = new URL(path, BASE);
  for (const key of Object.keys(params)) {
    if (url.searchParams.has(key)) url.searchParams.delete(key);
  }
  const kept = url.search.slice(1);
  const added = Object.entries(params)
    .map(([key, value]) => `${key}=${value}`)
    .join("&");
  return `${url.pathname}?${kept ? `${kept}&` : ""}${added}`;
}

/** The billing page, carrying the screen to return to and why she is here. */
export function billingPath({
  returnTo,
  notice,
}: {
  returnTo?: string;
  notice?: BillingNotice;
} = {}): string {
  const query = new URLSearchParams();
  if (returnTo) query.set("returnTo", returnTo);
  if (notice) query.set("notice", notice);
  const search = query.toString();
  return search ? `${BILLING_PATH}?${search}` : BILLING_PATH;
}
